import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useSignals, useStream } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClaimLedgerRow } from 'src/components/editorial/ClaimLedgerRow';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { compressionRatio } from 'src/lib/diff';
import { parseTimestampSeconds } from 'src/lib/time';
import type { EditorialNormalizedClaim, EditorialSignal } from 'src/types/editorial';

/**
 * Answers: what did the machine extract, and what did it change?
 *
 * "Lo dicho y lo escrito" is the split ledger — left the quote as it came out of the broadcast,
 * right the canonical claim the pipeline carries into the story.
 */

type SortBy = 'time' | 'compression';

/** A fresh `?? []` on every render defeats the memos below — this one is referentially stable. */
const NO_SIGNALS: EditorialSignal[] = [];

export function ClaimsWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const { data, isPending, isError, error, refetch } = useSignals(streamId);
  // Already warmed by StreamLayout under the same query key — this is a cache read, not a request.
  const { data: streamData } = useStream(streamId);
  const sourceUrl = streamData?.stream?.sourceUrl;
  const [sortBy, setSortBy] = useState<SortBy>('time');
  const [onlyWarned, setOnlyWarned] = useState(false);
  const [query, setQuery] = useState('');

  const signals = data?.signals ?? NO_SIGNALS;
  const claims = useMemo(() => sortClaims(collectClaims(signals), sortBy), [signals, sortBy]);
  const needle = query.trim().toLowerCase();
  const searched = needle
    ? claims.filter(
        claim =>
          claim.claim_text.toLowerCase().includes(needle) ||
          claim.evidence.excerpt.toLowerCase().includes(needle) ||
          claim.claim_id.toLowerCase().includes(needle)
      )
    : claims;
  const visible = onlyWarned ? searched.filter(claim => claim.warnings.length > 0) : searched;
  if (isPending) return <div className="page-wide"><Skeleton variant="ledger" count={6} /></div>;

  if (isError) {
    return (
      <div className="page-wide">
        <LoadFailure title="No se pudieron cargar los claims" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (claims.length === 0) {
    return (
      <div className="page-wide">
        <AbsenceState
          kind="not_reached"
          what="No hay claims normalizados."
          why="La normalización corre después de que termina el stream, sobre todas las señales del bloque."
          wouldChange="Cuando la transmisión cierre, el pipeline normaliza en lotes de 30 claims crudos."
        />
      </div>
    );
  }

  const filtering = needle !== '' || onlyWarned;

  return (
    <div className="page-wide">
      <section className="block" aria-labelledby="claims-title">
        <div className="tx-head">
          <h2 id="claims-title">Lo dicho y lo escrito</h2>
          <span className="tx-count">
            {visible.length} de {claims.length}
          </span>
        </div>
        <p className="claims-lead">
          Izquierda: la frase como salió del guion. Derecha: el claim canónico que el pipeline arrastra hasta la
          nota. Lo tenue se perdió; lo resaltado lo puso el modelo.
        </p>

        <section className="tx-filters" aria-label="Filtros del listado de claims">
          <div className="tx-field tx-search">
            <label htmlFor="claims-q">Buscar</label>
            <div className="tx-search-box">
              <span className="tx-search-icon" aria-hidden="true">
                <Icon name="search" />
              </span>
              <input
                id="claims-q"
                type="search"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Buscar en lo dicho o en lo normalizado"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="tx-field tx-field--select">
            <label htmlFor="claims-sort">Ordenar</label>
            <select id="claims-sort" value={sortBy} onChange={event => setSortBy(event.target.value as SortBy)}>
              <option value="time">Por momento</option>
              <option value="compression">Por compresión</option>
            </select>
          </div>
          <label className="tx-check">
            <input type="checkbox" checked={onlyWarned} onChange={event => setOnlyWarned(event.target.checked)} />
            <span>Solo con advertencia</span>
          </label>
          <button
            className="tx-clear"
            type="button"
            onClick={() => {
              setQuery('');
              setOnlyWarned(false);
            }}
            disabled={!filtering}
          >
            Limpiar
          </button>
        </section>

        <div className="claims">
          <div className="claim-head" aria-hidden="true">
            <span>Cómo se dijo</span>
            <span>Cómo quedó normalizado</span>
          </div>
          {visible.map(claim => (
            <ClaimLedgerRow key={claim.claim_id} claim={claim} sourceUrl={sourceUrl} />
          ))}
          {visible.length === 0 && <p className="guion-empty">Ningún claim coincide con los filtros.</p>}
        </div>
      </section>

    </div>
  );
}

function collectClaims(signals: EditorialSignal[]): EditorialNormalizedClaim[] {
  const seen = new Set<string>();
  const claims: EditorialNormalizedClaim[] = [];
  for (const signal of signals) {
    for (const claim of signal.payload.normalized_claims) {
      if (seen.has(claim.claim_id)) continue;
      seen.add(claim.claim_id);
      claims.push(claim);
    }
  }
  return claims;
}

function sortClaims(claims: EditorialNormalizedClaim[], sortBy: SortBy): EditorialNormalizedClaim[] {
  const sorted = [...claims];
  if (sortBy === 'compression') {
    // Composition §5: heaviest compression first — that is where distortion lives.
    sorted.sort((a, b) => {
      const delta =
        compressionRatio(a.evidence.excerpt, a.claim_text) - compressionRatio(b.evidence.excerpt, b.claim_text);
      return delta !== 0 ? delta : a.claim_id.localeCompare(b.claim_id);
    });
    return sorted;
  }
  sorted.sort((a, b) => {
    const delta =
      (parseTimestampSeconds(a.evidence.timestamps[0]) ?? 0) - (parseTimestampSeconds(b.evidence.timestamps[0]) ?? 0);
    // Deterministic tie-break so two loads produce the same order (Composition §13).
    return delta !== 0 ? delta : a.claim_id.localeCompare(b.claim_id);
  });
  return sorted;
}
